import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GroupsPeriodBadgePicker from './GroupsPeriodBadgePicker';
import GroupsFloatingAddBtn from './GroupsFloatingAddBtn';
import GroupsAddMemberEmptyState from './GroupsAddMemberEmptyState';
import { getMemberAccessDetails } from '../../utils/memberAccessDetails';

const memberDisplayName = (member) =>
  [member.first_name, member.last_name].filter(Boolean).join(' ').trim() ||
  member.email ||
  'Unnamed';

const sortMembersByName = (a, b) =>
  memberDisplayName(a).localeCompare(memberDisplayName(b), undefined, { sensitivity: 'base' });

const MEMBER_ACCESS_LEGEND = [
  {
    title: 'Login',
    items: [
      'Green check + Login — user.can_login is true (can sign in to any product).',
      'Red cancel + Login — user.can_login is false.'
    ]
  },
  {
    title: 'Access grant',
    items: [
      'Valid by override date range — override_period is on; ugp start/end are in range today.',
      'Valid by Groups-period date range — no override; linked period dates are in range today.',
      'Invalidated (inactive) — ugp.is_active is false.',
      'Pending / Expired by … — same sources, but before start or after end.'
    ]
  },
  {
    title: 'Dates',
    items: [
      'Click the access card on a member row to show or hide that membership’s UGP and Period date ranges.'
    ]
  }
];

const MemberAccessInfoButton = () => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="groups-dms-member-access-info" ref={wrapRef}>
      <button
        type="button"
        className="groups-info-bubble"
        aria-label="About member access badges"
        title="About member access badges"
        aria-expanded={open}
        aria-controls="groups-member-access-legend"
        onClick={() => setOpen((v) => !v)}
      >
        i
      </button>
      {open ? (
        <div
          id="groups-member-access-legend"
          className="groups-dms-member-access-legend"
          role="dialog"
          aria-label="Member access badge legend"
        >
          <p className="groups-dms-member-access-legend-title">Member access badges</p>
          {MEMBER_ACCESS_LEGEND.map((section) => (
            <div key={section.title} className="groups-dms-member-access-legend-section">
              <strong>{section.title}</strong>
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const BadgeIcon = ({ icon }) => {
  if (icon === 'check') {
    return (
      <svg className="groups-dms-member-access-badge-icon" viewBox="0 0 16 16" aria-hidden>
        <path
          fill="currentColor"
          d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm3.07 4.78-3.6 3.6a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.97.97 3.07-3.07a.75.75 0 1 1 1.06 1.06z"
        />
      </svg>
    );
  }
  if (icon === 'cancel') {
    return (
      <svg className="groups-dms-member-access-badge-icon" viewBox="0 0 16 16" aria-hidden>
        <path
          fill="currentColor"
          d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm2.28 3.72a.75.75 0 0 1 0 1.06L9.06 8l1.22 1.22a.75.75 0 1 1-1.06 1.06L8 9.06 6.78 10.28a.75.75 0 1 1-1.06-1.06L6.94 8 5.72 6.78a.75.75 0 0 1 1.06-1.06L8 6.94l1.22-1.22a.75.75 0 0 1 1.06 0z"
        />
      </svg>
    );
  }
  return null;
};

const MemberAccessCard = ({ member, period }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const access = getMemberAccessDetails(member, period);
  const metaId = `member-access-meta-${member.ugp_id || member.id}`;

  const toggleDetails = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDetailsOpen((open) => !open);
  };

  const blockParentNav = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <aside
      className={`groups-dms-member-access-card${detailsOpen ? ' is-expanded' : ''}`}
      aria-label={`Membership access — click to ${detailsOpen ? 'hide' : 'show'} UGP / Period dates`}
      aria-expanded={detailsOpen}
      aria-controls={metaId}
      role="button"
      tabIndex={0}
      title={`Click to ${detailsOpen ? 'hide' : 'show'} UGP / Period dates`}
      onClick={toggleDetails}
      onMouseDown={blockParentNav}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          toggleDetails(event);
        }
      }}
    >
      <div className="groups-dms-member-access-badges">
        {access.badges.map((badge) => (
          <span
            key={badge.key}
            className={`groups-dms-member-access-badge groups-dms-member-access-badge--${badge.tone}${detailsOpen ? ' is-active' : ''}`}
            title={badge.title}
          >
            <BadgeIcon icon={badge.icon} />
            <span>{badge.label}</span>
          </span>
        ))}
      </div>
      {detailsOpen ? (
        <div id={metaId} className="groups-dms-member-access-meta">
          <span title="UGP start/end dates">
            UGP {access.ugpRangeLabel || '—'}
          </span>
          <span title="Group-period dates">
            Period {access.periodRangeLabel || '—'}
          </span>
        </div>
      ) : null}
    </aside>
  );
};

const GroupsMembersPanel = ({
  periods = [],
  periodValue,
  onPeriodChange,
  periodDisabled = false,
  canAddMembers = false,
  canOpenMemberProfiles = false,
  canViewGroupMembers = false,
  canViewMemberDetails = false,
  canViewMemberAccess = false,
  currentUserId = null,
  onAddMember,
  members = [],
  loading = false,
  emptyMessage = 'No members in this group.',
  othersHiddenMessage = 'Unable to see other members in this group.',
  selfOnlyEmptyMessage = 'You are not a member of this group for the selected period.',
  returnUrlBase = '/groups'
}) => {
  const [search, setSearch] = useState('');

  const selectedPeriod = useMemo(
    () => (periods || []).find((p) => String(p.id) === String(periodValue)) || null,
    [periods, periodValue]
  );

  const rosterMembers = useMemo(() => {
    if (canViewGroupMembers) return members;
    if (!currentUserId) return [];
    return members.filter((member) => String(member.id) === String(currentUserId));
  }, [members, canViewGroupMembers, currentUserId]);

  const othersHidden = !canViewGroupMembers;
  const showDetailsForMember = (member) =>
    canViewMemberDetails || String(member.id) === String(currentUserId);
  const showAccessForMember = (member) =>
    canViewMemberAccess || String(member.id) === String(currentUserId);

  const showSearch = canViewGroupMembers && rosterMembers.length > 10;

  const filtered = useMemo(() => {
    const sorted = [...rosterMembers].sort(sortMembersByName);
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((m) => {
      const name = memberDisplayName(m).toLowerCase();
      if (name.includes(q)) return true;
      const showDetails =
        canViewMemberDetails || String(m.id) === String(currentUserId);
      if (!showDetails) return false;
      const email = (m.email || '').toLowerCase();
      return email.includes(q);
    });
  }, [rosterMembers, search, canViewMemberDetails, currentUserId]);

  const showAddMemberEmpty =
    !loading &&
    canViewGroupMembers &&
    members.length === 0 &&
    canAddMembers &&
    periodValue &&
    !search.trim();

  const showFloatingAdd =
    canAddMembers && periodValue && !showAddMemberEmpty && !loading && canViewGroupMembers;

  const rosterCount = rosterMembers.length;
  const memberCountLabel = useMemo(() => {
    if (loading) return null;
    const q = search.trim();
    if (q && filtered.length !== rosterCount) {
      return `${filtered.length} of ${rosterCount} member${rosterCount === 1 ? '' : 's'}`;
    }
    return `${rosterCount} member${rosterCount === 1 ? '' : 's'}`;
  }, [loading, search, filtered.length, rosterCount]);

  return (
    <section className="groups-dms-members">
      <div className="groups-dms-members-toolbar">
        {periods.length > 0 && (
          <GroupsPeriodBadgePicker
            periods={periods}
            value={periodValue}
            onChange={(period) => onPeriodChange?.(period?.id ?? period)}
            disabled={periodDisabled}
          />
        )}
        <div className="groups-dms-members-toolbar-end">
          {canViewMemberAccess ? <MemberAccessInfoButton /> : null}
          {memberCountLabel ? (
            <span className="groups-dms-members-count" aria-live="polite">
              {memberCountLabel}
            </span>
          ) : loading ? (
            <span
              className="groups-dms-members-count groups-dms-members-count--loading"
              role="status"
              aria-label="Loading members"
            >
              <span className="loading-spinner groups-dms-members-count-spinner" aria-hidden />
            </span>
          ) : null}
          {showSearch && (
            <input
              type="search"
              className="groups-search groups-search--compact groups-dms-members-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search members…"
            />
          )}
        </div>
      </div>

      <div className="groups-members-body">
        {loading ? (
          <div
            className="groups-members-loading"
            role="status"
            aria-live="polite"
            aria-label="Loading members"
          >
            <div className="loading-spinner loading-spinner--sm" />
          </div>
        ) : showAddMemberEmpty ? (
          <div className="groups-members-empty-canvas">
            <GroupsAddMemberEmptyState onClick={onAddMember} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="groups-members-restricted">
            {othersHidden && (
              <p className="groups-members-others-hidden-notice">{othersHiddenMessage}</p>
            )}
            <p className="groups-dms-muted groups-members-body-message">
              {othersHidden
                ? selfOnlyEmptyMessage
                : members.length === 0
                  ? emptyMessage
                  : 'No members match your search.'}
            </p>
          </div>
        ) : (
          <div className="groups-members-restricted">
            {othersHidden && (
              <p className="groups-members-others-hidden-notice">{othersHiddenMessage}</p>
            )}
            <div className="groups-dms-member-list">
            {filtered.map((member) => {
              const name = memberDisplayName(member);
              const showAccess = showAccessForMember(member);
              const rowClass = showAccess
                ? 'groups-dms-member-row groups-dms-member-row--with-access'
                : 'groups-dms-member-row';
              const content = (
                <>
                  <div className="groups-dms-member-identity">
                    <p className="groups-dms-member-name">{name}</p>
                    {showDetailsForMember(member) && member.email && (
                      <p className="groups-dms-member-email">{member.email}</p>
                    )}
                  </div>
                  {showAccess && (
                    <MemberAccessCard member={member} period={selectedPeriod} />
                  )}
                </>
              );
              if (canOpenMemberProfiles) {
                const href = `/user-profiles?id=${member.id}&from=${encodeURIComponent(returnUrlBase)}`;
                return (
                  <a
                    key={member.ugp_id || `${member.id}-${member.groups_periods_id}`}
                    href={href}
                    className={rowClass}
                  >
                    {content}
                  </a>
                );
              }
              return (
                <div
                  key={member.ugp_id || `${member.id}-${member.groups_periods_id}`}
                  className={`${rowClass} groups-dms-member-row--static`}
                  aria-label={name}
                >
                  {content}
                </div>
              );
            })}
            </div>
          </div>
        )}

      </div>
      {showFloatingAdd
        ? createPortal(
            <GroupsFloatingAddBtn
              className="groups-org-floating-add-btn--members"
              onClick={onAddMember}
              ariaLabel="Add member"
              title="Add member to this group-period"
            />,
            document.body
          )
        : null}
    </section>
  );
};

export default GroupsMembersPanel;
